import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  type Encounter,
  useCreateTranscriptionSession,
  useGetEncounterTranscriptLastOffset,
} from "@elation/api-client/src/stitch-web";
import {
  type AiScribeStep,
  type ClientNoteTranscriptItem,
  isErrorStep,
  isFinishingStep,
  isPausedStep,
  isRecordingActiveStep,
  isScribeSocketLiveStep,
  resetScribingSessionId,
  useAudioInputDeviceSelect,
  useClearTranscriptItems,
  useScribe,
  useScribeReady,
  useStep,
  useTeardown,
} from "@elation/transcribe";

import { useIsInElationEHR } from "@src/features/iframe";
import { useActor } from "@src/shared/providers/actorProvider";
import { STRING_ID_NOT_READY } from "@src/shared/utils/constants";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";
import { datadogLogs } from "@src/shared/utils/observability/datadog";

import { registerEncounterId } from "../domain";
import {
  useEncounterId,
  useIsEncounterNoteGenerationActive,
} from "./useEncounterActorSelectors";
import {
  resolveVisitRecordingStep,
  type VisitRecordingV2,
} from "./visit-recording";

const DEFAULT_WEBSOCKET_URL = "ws://localhost:8080/ws/transcribe";
const INITIAL_SCRIBE_STEP = "initial" satisfies AiScribeStep;

interface SessionRequestSnapshot {
  generation: number;
  patientId: number | undefined;
  encounterId: number | undefined;
}

function reportVisitActionError(
  errorId: string,
  error: unknown,
  fallbackMessage: string,
  context?: Record<string, unknown>,
) {
  datadogLogs.logger.error(
    errorId,
    { errorId, ...context },
    error instanceof Error ? error : undefined,
  );
  toast.error(getServerErrorMessage(error) ?? fallbackMessage);
}

export interface UseVisitRecordingSessionInput {
  encounterId: number | undefined;
  patientId: number | undefined;
  onEncounterUpdated?: (encounter: Encounter) => void;
}

export interface UseVisitRecordingSessionResult {
  recording: VisitRecordingV2;
  recordingStep: AiScribeStep;
  scribingSessionId: string | null;
  isRecordingLive: boolean;
  isScribeInProgress: boolean;
  activeEncounterId: number | undefined;
  startVisitRecording: () => Promise<void>;
  resumeVisitRecording: () => Promise<void>;
  stopVisitRecording: () => void;
  resetScribeForEncounter: (nextEncounterId: number | undefined) => void;
}

/**
 * Shared scribe/recording session for Visit route and patient-detail chrome.
 */
export function useVisitRecordingSession({
  encounterId,
  patientId,
  onEncounterUpdated,
}: UseVisitRecordingSessionInput): UseVisitRecordingSessionResult {
  const actor = useActor();
  const isInElationEHR = useIsInElationEHR();
  const [activeEncounterId, setActiveEncounterId] = useState<
    number | undefined
  >(undefined);
  const sessionRequestGenerationRef = useRef(0);
  const patientIdRef = useRef(patientId);
  const encounterIdRef = useRef(encounterId);

  patientIdRef.current = patientId;
  encounterIdRef.current = encounterId;

  useEffect(() => {
    sessionRequestGenerationRef.current += 1;
  }, [patientId, encounterId]);

  useEffect(() => {
    return () => {
      sessionRequestGenerationRef.current += 1;
    };
  }, []);

  const beginSessionRequest = useCallback((): SessionRequestSnapshot => {
    sessionRequestGenerationRef.current += 1;
    return {
      generation: sessionRequestGenerationRef.current,
      patientId,
      encounterId,
    };
  }, [encounterId, patientId]);

  /**
   * Returns true when an in-flight start/resume may still apply side effects.
   * Stale generations (unmount, patient/encounter change, newer request) and
   * abandoned created sessions are dropped without starting recording.
   */
  const isSessionRequestCurrent = useCallback(
    (request: SessionRequestSnapshot) => {
      return (
        request.generation === sessionRequestGenerationRef.current &&
        patientIdRef.current === request.patientId &&
        encounterIdRef.current === request.encounterId
      );
    },
    [],
  );

  const {
    mutateAsync: createTranscriptionSession,
    data: createTranscriptionSessionData,
    reset: resetCreateTranscriptionSession,
  } = useCreateTranscriptionSession();

  const currentTranscriptionSession =
    createTranscriptionSessionData &&
    createTranscriptionSessionData.data.encounter.id === encounterId
      ? createTranscriptionSessionData.data
      : null;
  const scribingSessionId =
    currentTranscriptionSession?.scribingSession.id ?? null;
  const el8TranscribeSessionId =
    currentTranscriptionSession?.scribingSession.el8TranscribeSessionId ?? null;

  const lastOffset = useGetEncounterTranscriptLastOffset(
    encounterId ?? 0,
    scribingSessionId ?? STRING_ID_NOT_READY,
    {
      query: {
        enabled: encounterId != null && scribingSessionId != null,
        refetchOnMount: "always",
        staleTime: Infinity,
      },
    },
  );

  const [step, setStep] = useStep();
  const isScribeReady = useScribeReady();
  const teardownScribe = useTeardown();
  const clearTranscriptItems = useClearTranscriptItems();
  const { selectedDeviceId: microphoneDeviceId } = useAudioInputDeviceSelect();
  const actorEncounterId = useEncounterId();
  const isEncounterNoteGenerationActive = useIsEncounterNoteGenerationActive();

  const isScribeReboundToEncounter =
    encounterId != null &&
    actorEncounterId === encounterId &&
    isEncounterNoteGenerationActive;
  const isScribeStateCurrent =
    activeEncounterId === encounterId || isScribeReboundToEncounter;
  const isScribeInProgress =
    isRecordingActiveStep(step) ||
    isPausedStep(step) ||
    isFinishingStep(step) ||
    isErrorStep(step);
  const recordingStep = resolveVisitRecordingStep({
    encounterId,
    activeEncounterId,
    step,
    isScribeStateCurrent,
    isScribeInProgress,
  });
  const isRecordingLive = isScribeSocketLiveStep(recordingStep);

  const getScribingSessionId = useCallback(async () => {
    if (isInElationEHR) {
      throw new Error("Scribing session is not supported in iframe mode");
    }

    if (el8TranscribeSessionId) {
      return el8TranscribeSessionId;
    }

    throw new Error("Scribing session not found");
  }, [el8TranscribeSessionId, isInElationEHR]);

  const saveTranscriptItem = useCallback(
    async (_input: {
      scribingSessionId: string;
      data: ClientNoteTranscriptItem;
    }): Promise<void> => {
      return;
    },
    [],
  );

  const scribeOptions = useMemo(
    () => ({
      websocketUrl:
        actor.config.el8TranscribeWebsocketUrl ?? DEFAULT_WEBSOCKET_URL,
      getScribingSessionId,
      saveTranscriptItem,
      lastOffset: lastOffset.data?.data?.offset ?? 0,
    }),
    [
      actor.config.el8TranscribeWebsocketUrl,
      getScribingSessionId,
      lastOffset.data?.data?.offset,
      saveTranscriptItem,
    ],
  );

  const {
    audioContext,
    audioSource,
    startRecording,
    pauseRecording,
    stopRecording,
  } = useScribe(scribeOptions);

  const startVisitRecording = useCallback(async () => {
    if (isInElationEHR) {
      toast.error("Transcription is not supported in iframe mode.");
      return;
    }

    if (patientId == null) {
      toast.error("Select a patient before starting the visit.");
      return;
    }

    if (!microphoneDeviceId) {
      toast.error("Select a microphone before starting the visit.");
      return;
    }

    if (el8TranscribeSessionId) {
      setActiveEncounterId(encounterId);
      if (encounterId != null) {
        registerEncounterId(encounterId);
      }

      if (isErrorStep(recordingStep)) {
        teardownScribe();
        clearTranscriptItems();
        setStep(INITIAL_SCRIBE_STEP);
      }

      startRecording(el8TranscribeSessionId);
      return;
    }

    if (isErrorStep(recordingStep)) {
      teardownScribe();
      clearTranscriptItems();
      setStep(INITIAL_SCRIBE_STEP);
    }

    const request = beginSessionRequest();

    try {
      const result = await createTranscriptionSession({
        data: {
          patientId,
        },
      });

      if (!isSessionRequestCurrent(request)) {
        return;
      }

      registerEncounterId(result.data.encounter.id);
      setActiveEncounterId(result.data.encounter.id);
      onEncounterUpdated?.(result.data.encounter);
      startRecording(result.data.scribingSession.el8TranscribeSessionId);
    } catch (error) {
      if (!isSessionRequestCurrent(request)) {
        return;
      }

      reportVisitActionError(
        "visit_v2.start_recording_failed",
        error,
        "Couldn't start the visit. Please try again.",
        { encounterId, patientId },
      );
    }
  }, [
    beginSessionRequest,
    clearTranscriptItems,
    createTranscriptionSession,
    el8TranscribeSessionId,
    encounterId,
    isInElationEHR,
    isSessionRequestCurrent,
    microphoneDeviceId,
    onEncounterUpdated,
    patientId,
    recordingStep,
    setStep,
    startRecording,
    teardownScribe,
  ]);

  const resumeVisitRecording = useCallback(async () => {
    const request = beginSessionRequest();

    try {
      const sessionId = await getScribingSessionId();

      if (!isSessionRequestCurrent(request)) {
        return;
      }

      setActiveEncounterId(encounterId);
      if (encounterId != null) {
        registerEncounterId(encounterId);
      }
      startRecording(sessionId);
    } catch (error) {
      if (!isSessionRequestCurrent(request)) {
        return;
      }

      reportVisitActionError(
        "visit_v2.resume_recording_failed",
        error,
        "Couldn't resume the visit. Please try again.",
        { encounterId },
      );
    }
  }, [
    beginSessionRequest,
    encounterId,
    getScribingSessionId,
    isSessionRequestCurrent,
    startRecording,
  ]);

  const stopVisitRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const resetScribeForEncounter = useCallback(
    (nextEncounterId: number | undefined) => {
      setActiveEncounterId(nextEncounterId);
      resetCreateTranscriptionSession();
      if (isScribeInProgress) return;

      resetScribingSessionId();
      clearTranscriptItems();
      setStep(INITIAL_SCRIBE_STEP);
    },
    [
      clearTranscriptItems,
      isScribeInProgress,
      resetCreateTranscriptionSession,
      setStep,
    ],
  );

  const recording: VisitRecordingV2 = {
    step: recordingStep,
    isReady: isScribeReady && Boolean(microphoneDeviceId),
    isSupported: !isInElationEHR,
    microphoneDeviceId,
    audioContext,
    audioSource,
    needsMicrophoneSelection:
      !isInElationEHR &&
      recordingStep === INITIAL_SCRIBE_STEP &&
      !microphoneDeviceId,
    errorMessage: isErrorStep(recordingStep)
      ? "Check microphone permissions, then try again."
      : null,
    onStart: () => void startVisitRecording(),
    onPause: pauseRecording,
    onResume: () => void resumeVisitRecording(),
    onStop: stopVisitRecording,
  };

  return {
    recording,
    recordingStep,
    scribingSessionId,
    isRecordingLive,
    isScribeInProgress,
    activeEncounterId,
    startVisitRecording,
    resumeVisitRecording,
    stopVisitRecording,
    resetScribeForEncounter,
  };
}
